import { action, makeObservable, observable } from 'mobx';
import { getEnv, getRoot } from 'mobx-easy';
// deep import: keep the icon package's component code out of the store import chain
import { BringgFontIcons } from '@bringg/bringg-icons/dist/types/icon-types';

import RootStore from '../root-store';
import { RootEnv } from '../../helpers/create-store';
import DateService from '../../services/date-service';
import { ChatMessage, ChatMessageSender, QuickReply } from '../../components/chat/chat-types';
import { RescheduleOption, ScheduleUpdateParams } from '../../types/shared-location.types';
import {
	CONFIRMED_STEP_ID,
	createRescheduleFlow,
	EXIT_REPLY_ID,
	exitReply,
	FlowReply,
	RescheduleContext,
	RescheduleFlow,
	START_STEP_ID,
	TRY_AGAIN_REPLY_ID
} from './reschedule-flow';

class RescheduleStore {
	messages: ChatMessage[] = [];
	quickReplies: QuickReply[] = [];
	isTyping: boolean = false;

	currentStepId: string = null;

	private flow: RescheduleFlow = null;
	private context: RescheduleContext = null;
	private messageIdCounter = 0;
	// Lets "Try again" re-submit the same window rather than reload the options.
	private pendingOption: RescheduleOption = null;
	// Bumped on every start(), so a response from a conversation the customer already left
	// cannot write its options or its error message into the one now on screen.
	private sessionId = 0;

	constructor() {
		makeObservable(this, {
			messages: observable,
			quickReplies: observable,
			isTyping: observable,
			currentStepId: observable,
			start: action,
			selectQuickReply: action,
			setTyping: action,
			setQuickReplies: action,
			onOptionsLoaded: action,
			onConfirmed: action,
			onError: action
		});
	}

	start = (context: RescheduleContext) => {
		this.sessionId++;
		this.context = context;
		this.flow = null;
		this.currentStepId = null;
		this.pendingOption = null;
		this.messages = [];
		this.quickReplies = [];

		const deliveryWindow = context.deliveryWindowStart
			? DateService.formatWindow(context.deliveryWindowStart, context.deliveryWindowEnd, context.timezone)
			: '';

		this.appendMessage(
			'assistant',
			this.translate('RESCHEDULE.GREETING', {
				merchant_name: context.merchantName,
				order_id: context.orderId,
				delivery_window: deliveryWindow
			})
		);

		this.load();
	};

	selectQuickReply = (replyId: string) => {
		if (replyId === EXIT_REPLY_ID) {
			this.context?.onExit?.();
			return;
		}

		if (replyId === TRY_AGAIN_REPLY_ID) {
			this.setQuickReplies([]);

			if (this.pendingOption) {
				this.submit(this.pendingOption);
			} else {
				this.load();
			}
			return;
		}

		const reply = this.flow?.[this.currentStepId]?.replies.find(({ id }) => id === replyId);

		if (!reply) {
			return;
		}

		this.appendMessage('customer', this.resolveReplyLabel(reply));

		if (reply.option) {
			this.submit(reply.option);
			return;
		}

		this.goToStep(reply.nextStepId, reply.nextArgs);
	};

	setTyping = (isTyping: boolean) => {
		this.isTyping = isTyping;
	};

	setQuickReplies = (quickReplies: QuickReply[]) => {
		this.quickReplies = quickReplies;
	};

	onOptionsLoaded = (options: RescheduleOption[]) => {
		this.setTyping(false);

		if (!options.length) {
			this.appendMessage('assistant', this.translate('RESCHEDULE.NO_WINDOWS_AVAILABLE'));
			this.setQuickReplies([this.exitQuickReply()]);
			return;
		}

		this.flow = createRescheduleFlow(this.context, options);
		this.goToStep(START_STEP_ID);
	};

	onConfirmed = (deliveryWindow: string) => {
		this.setTyping(false);
		this.pendingOption = null;
		this.goToStep(CONFIRMED_STEP_ID, { delivery_window: deliveryWindow });

		// The reschedule itself already succeeded. Re-reading the order is only how the screen behind
		// the chat learns about it, so if that read fails we log it and leave the confirmation
		// standing — routing it to onError would tell the customer their reschedule failed when it did not.
		getRoot<RootStore>().refreshCustomerExperience().catch(console.error);
	};

	// Shared by the load and submit paths; pendingOption decides which one "Try again" retries.
	onError = () => {
		this.setTyping(false);
		this.appendMessage('assistant', this.translate('RESCHEDULE.SOMETHING_WENT_WRONG'));
		this.setQuickReplies([
			{ id: TRY_AGAIN_REPLY_ID, label: this.translate('RESCHEDULE.TRY_AGAIN'), icon: BringgFontIcons.Reload },
			this.exitQuickReply()
		]);
	};

	// The async continuations (onOptionsLoaded/onError) are actions in their own right — MobX will
	// not treat them as part of the calling action.
	private load() {
		this.setTyping(true);
		this.setQuickReplies([]);

		const session = this.sessionId;

		getEnv<RootEnv>()
			.sharedLocationService.getRescheduleOptions(this.context.optionsUrl)
			.then(options => this.inSession(session, () => this.onOptionsLoaded(options)))
			.catch(error => {
				console.error(error);
				this.inSession(session, this.onError);
			});
	}

	private submit(option: RescheduleOption) {
		this.pendingOption = option;
		this.setTyping(true);
		this.setQuickReplies([]);

		const params: ScheduleUpdateParams = {
			delivery_window_id: option.id,
			start_time: option.start_time,
			end_time: option.end_time
		};
		const deliveryWindow = DateService.formatWindow(option.start_time, option.end_time, this.context.timezone);
		const session = this.sessionId;

		getEnv<RootEnv>()
			.sharedLocationService.updateReschedule(params, this.context.updateUrl)
			.then(() => this.inSession(session, () => this.onConfirmed(deliveryWindow)))
			.catch(error => {
				console.error(error);
				this.inSession(session, this.onError);
			});
	}

	private inSession(session: number, apply: () => void) {
		if (session === this.sessionId) {
			apply();
		}
	}

	private goToStep(stepId: string, args?: Record<string, unknown>) {
		const step = this.flow[stepId];

		this.currentStepId = stepId;

		for (const message of step.messages) {
			this.appendMessage('assistant', this.translate(message.key, { ...message.args, ...args }));
		}

		this.setQuickReplies(
			step.replies.map(reply => ({
				id: reply.id,
				label: this.resolveReplyLabel(reply),
				icon: reply.icon,
				ownRow: reply.ownRow
			}))
		);
	}

	private appendMessage(sender: ChatMessageSender, text: string) {
		this.messages.push({
			id: `message-${++this.messageIdCounter}`,
			type: 'text',
			sender,
			text,
			timestamp: new Date()
		});
	}

	private exitQuickReply(): QuickReply {
		return { id: exitReply.id, label: this.resolveReplyLabel(exitReply), icon: exitReply.icon, ownRow: true };
	}

	private resolveReplyLabel(reply: FlowReply): string {
		return reply.labelKey ? this.translate(reply.labelKey) : reply.label;
	}

	private translate(key: string, args: Record<string, unknown> = {}): string {
		return getEnv<RootEnv>().langService.translate(key, args);
	}
}

export default RescheduleStore;
